import { useRef, useState } from "react";
import { authClient } from "../../lib/auth-client";
import { isNative } from "../../lib/platform";
import { signInWithNativeProvider } from "../../lib/nativeSocialLogin";
import { rememberSignupMethod } from "../../lib/appsflyer";
import { getMetaAttribution, MetaAttribution } from "../../lib/metaPixel";

/**
 * Shared social-auth actions for LoginPage, RegisterPage and the onboarding
 * auth sheet — one copy of the OAuth re-entrancy guard, the native ID-token
 * flow, and the load-bearing hard reload.
 *
 * Native social success MUST end in `window.location.href = "/dashboard"`
 * (never navigate()): better-auth's client only refetches its useSession
 * store after /sign-in/email etc. — NOT /sign-in/social — so an SPA
 * navigation lands on the route guards with the stale null session and
 * bounces back. A full load re-hydrates from the persisted bearer token.
 */

type Provider = "apple" | "google";

const PROVIDER_LABELS: Record<Provider, string> = {
  apple: "Apple",
  google: "Google",
};

// Apple's callback is a cross-site POST that carries no Lax cookies, so the
// click attribution rides in the OAuth state instead; omitted when there is
// nothing to carry so the state stays small.
const oauthAdditionalData = (): MetaAttribution | undefined => {
  const attribution = getMetaAttribution();
  const hasValue = Object.values(attribution).some((value) => value !== undefined);
  return hasValue ? attribution : undefined;
};

export const useAuthActions = (mode: "sign-in" | "sign-up") => {
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Ref to prevent multiple simultaneous OAuth attempts (refs update
  // synchronously unlike state) — guards against double-clicks and race
  // conditions that cause state_mismatch errors.
  const isOAuthInProgressRef = useRef(false);

  const verb = mode === "sign-in" ? "sign in" : "sign up";

  const handleNativeSocial = async (provider: Provider) => {
    try {
      await signInWithNativeProvider(provider);
      // Survives the reload below so the attribution registration event can be
      // labelled; only read on a first authenticated visit, so a returning
      // user signing in never produces one.
      rememberSignupMethod(provider);
      window.location.href = "/dashboard";
    } catch (socialError: any) {
      setError(
        socialError?.message ?? `Failed to ${verb} with ${PROVIDER_LABELS[provider]}`,
      );
    } finally {
      setIsLoading(false);
      isOAuthInProgressRef.current = false;
    }
  };

  const signInWithSocial = async (provider: Provider) => {
    if (isOAuthInProgressRef.current) {
      return;
    }
    isOAuthInProgressRef.current = true;

    setError("");
    setIsLoading(true);

    if (isNative()) {
      await handleNativeSocial(provider);
      return;
    }

    await authClient.signIn.social(
      {
        provider,
        callbackURL: `${window.location.origin}/dashboard`,
        additionalData: oauthAdditionalData(),
      },
      {
        onError: (ctx) => {
          setError(ctx.error.message ?? `Failed to ${verb} with ${PROVIDER_LABELS[provider]}`);
          setIsLoading(false);
          isOAuthInProgressRef.current = false; // Reset on error so user can retry
        },
      },
    );
  };

  return {
    error,
    setError,
    isLoading,
    setIsLoading,
    signInWithApple: () => signInWithSocial("apple"),
    signInWithGoogle: () => signInWithSocial("google"),
  };
};
